import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { staffAPI } from '../services/api';

interface Permission {
  id: string;
  name: string;
  description: string;
  resource: string;
  action: string;
  isSystem: boolean;
}

interface PermissionContextType {
  permissions: Permission[];
  loading: boolean;
  error: string | null;
  hasPermission: (resource: string, action: string) => boolean;
  hasAnyPermission: (permissions: Array<{ resource: string; action: string }>) => boolean;
  hasAllPermissions: (permissions: Array<{ resource: string; action: string }>) => boolean;
  canAccess: (resource: string, action: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isAuthenticated } = useAuth();

  const fetchUserPermissions = async () => {
    if (!isAuthenticated || !user) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Fetch user permissions from the backend
      const response = await staffAPI.getUserPermissions();
      const userPermissions = response.data.data || [];
      
      setPermissions(userPermissions);
    } catch (error: any) {
      console.error('Error fetching user permissions:', error);
      setError(error.response?.data?.message || 'Failed to fetch permissions');
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshPermissions = async () => {
    await fetchUserPermissions();
  };

  useEffect(() => {
    fetchUserPermissions();
  }, [isAuthenticated, user?.id]);

  const hasPermission = (resource: string, action: string): boolean => {
    if (!user || user.role === 'ADMIN') {
      return true; // Admin has all permissions
    }

    return permissions.some(permission => 
      permission.resource === resource && permission.action === action
    );
  };

  const hasAnyPermission = (permissions: Array<{ resource: string; action: string }>): boolean => {
    if (!user || user.role === 'ADMIN') {
      return true; // Admin has all permissions
    }

    return permissions.some(({ resource, action }) => 
      hasPermission(resource, action)
    );
  };

  const hasAllPermissions = (permissions: Array<{ resource: string; action: string }>): boolean => {
    if (!user || user.role === 'ADMIN') {
      return true; // Admin has all permissions
    }

    return permissions.every(({ resource, action }) => 
      hasPermission(resource, action)
    );
  };

  const canAccess = (resource: string, action: string): boolean => {
    return hasPermission(resource, action);
  };

  const value: PermissionContextType = {
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccess,
    refreshPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
